import AppIcon from "../components/AppIcon";
import { useState, useEffect } from "react";
import { IMAGES } from "../constants/images";
import Sidebar from "../components/Sidebar";
import { LuSearch as CiSearch } from "react-icons/lu";
import CommunitySearchItem from "../components/CommunitySearchItem";
import { ref, onValue, off } from "firebase/database";
import { database } from "../constants/firebase";
import useMoveTo from "../constants/navigation";
import { useUser } from "../contexts/UserContext";
import MobileHeader from "../components/MobileHeader";

const CommunitySearch = () => {
    const navigate = useMoveTo();
    const { currentUser } = useUser(); // ✅ get current user
    const [searchQuery, setSearchQuery] = useState("");
    const [communities, setCommunities] = useState([]);
    const [filteredCommunities, setFilteredCommunities] = useState([]);
    const [loading, setLoading] = useState(true);

    // Fetch communities and their last messages from Firebase
    useEffect(() => {
        const communitiesRef = ref(database, 'Communities'); // Fixed typo
        const unsubscribe = onValue(communitiesRef, (snapshot) => {
            const communitiesData = snapshot.val() || {};

            const communitiesList = Object.entries(communitiesData).map(([id, community]) => ({
                id,
                ...community
            }));

            const fetchPromises = communitiesList.map(async (community) => {
                try {
                    const messagesRef = ref(database, `communityMessages/${community.id}`);
                    const lastMessage = await getLastMessage(messagesRef);

                    const currentUserId = currentUser?.uid;

                    const isUnread =
                        lastMessage &&
                        lastMessage.senderId !== currentUserId && // don’t count your own messages
                        (!lastMessage.isSeen || !lastMessage.isSeen[currentUserId]);

                    return {
                        ...community,
                        lastMessage: lastMessage?.text
                            ? lastMessage.text
                            : lastMessage?.imageUrl
                                ? "📷 Image"
                                : "No messages yet",
                        lastMessageTimestamp: lastMessage?.timestamp || community.createdAt || 0,
                        isUnread, // ✅ add flag
                    };
                } catch (error) {
                    console.error(`Error fetching messages for community ${community.id}:`, error);
                    return {
                        ...community,
                        lastMessage: "Error loading messages",
                        lastMessageTimestamp: community.createdAt || 0,
                        isUnread: false,
                    };
                }
            });

            Promise.all(fetchPromises).then(communitiesWithMessages => {
                // Sort by last message timestamp (newest first)
                communitiesWithMessages.sort((a, b) => b.lastMessageTimestamp - a.lastMessageTimestamp);
                setCommunities(communitiesWithMessages);
                setFilteredCommunities(communitiesWithMessages);
                setLoading(false);
            });
        });

        return () => off(communitiesRef);
    }, []);

    // Improved getLastMessage function
    const getLastMessage = async (messagesRef) => {
        return new Promise((resolve) => {
            onValue(messagesRef, (snapshot) => {
                const messages = snapshot.val();
                if (!messages) {
                    resolve(null);
                    return;
                }

                const messageList = Object.values(messages);
                if (messageList.length === 0) {
                    resolve(null);
                    return;
                }

                // Find message with highest timestamp
                const lastMessage = messageList.reduce((latest, current) =>
                    current.timestamp > (latest?.timestamp || 0) ? current : latest
                );
                resolve(lastMessage);
            }, { onlyOnce: true });
        });
    };


    // Filter communities based on search query
    useEffect(() => {
        if (searchQuery) {
            const filtered = communities.filter(community =>
                community.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                community.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                community.hostName?.toLowerCase().includes(searchQuery.toLowerCase())
            );
            setFilteredCommunities(filtered);
        } else {
            setFilteredCommunities(communities);
        }
    }, [searchQuery, communities]);

    // 🟢 Simple shimmer skeleton loader component
    const ShimmerLoader = () => (
        <div className="flex flex-col space-y-4 mt-8">
            {[...Array(4)].map((_, i) => (
                <div
                    key={i}
                    className="animate-pulse flex items-center justify-between p-4 border-t border-gray-200 dark:border-[#D9D9D9]/20"
                >
                    <div className="flex items-center space-x-4">
                        <div className="w-16 h-16 bg-gray-300 dark:bg-gray-700 rounded-full"></div>
                        <div>
                            <div className="w-40 h-4 bg-gray-300 dark:bg-gray-700 rounded mb-2"></div>
                            <div className="w-40 h-4 bg-gray-300 dark:bg-gray-700 rounded mb-2"></div>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );

    return (
        <div className="h-screen bg-white text-black dark:bg-[#23272c] dark:text-white overflow-y-auto">
            <MobileHeader title="Search Community" isBackBtn={true} />

            <div className="flex">
                {/* Sidebar */}
                <Sidebar />

                {/* Main Content */}
                <main className="flex-1 h-screen bg-white text-black dark:bg-[#23272c] dark:text-white flex flex-col px-6 pt-4 md:pt-8 pb-4">
                    {/* Back + Search - fixed */}
                    <div className="flex items-center md:space-x-8 space-x-0 flex-shrink-0">
                        <button onClick={() => navigate(-1)} className="flex-shrink-0 md:block hidden">
                            <AppIcon name="arrowBack" className="w-5 h-5 brightness-0 dark:invert" alt="Back" />
                        </button>

                        <div className="relative flex-1">
                            <CiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-xl" />
                            <input
                                type="text"
                                placeholder="Search communities"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="rounded-xl pl-10 pr-4 py-2 bg-[#F5F9FE] dark:bg-white placeholder-[#6b7280] outline-none w-full text-black dark:text-black border border-gray-200 dark:border-none"
                            />
                        </div>
                    </div>

                    {/* Tab Content - scrollable */}
                    <div className="mt-8 flex-1 overflow-y-auto pr-1 pb-12">
                        {loading ? (
                            <ShimmerLoader />
                        ) : (
                            <CommunitySearchItem
                                communities={filteredCommunities}
                                navigate={navigate}
                            />
                        )}
                    </div>
                </main>
            </div>
        </div>
    );
};

export default CommunitySearch;